import { Button, cn } from '@crm/ui';
import { CalendarPlus, ChevronDown, Link2, Plus } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

interface CreateMenuProps {
  className?: string;
  onNewAppointment: () => void;
  onNewLink: () => void;
}

/**
 * O "Criar" da agenda abre duas portas: marcar o horario agora ou mandar o
 * link para o cliente escolher. As duas levam a mesma modal - muda quem
 * termina de preencher.
 */
export function CreateMenu({ className, onNewAppointment, onNewLink }: CreateMenuProps) {
  const [isOpen, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  // Clique fora e Esc fecham, como em qualquer menu do sistema.
  useEffect(() => {
    if (!isOpen) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!boxRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  return (
    <div ref={boxRef} className={cn('relative', className)}>
      <Button
        className="w-full justify-start rounded-full shadow-sm"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={() => setOpen((open) => !open)}
      >
        <Plus className="h-4 w-4" />
        Criar
        <ChevronDown className="ml-auto h-4 w-4" />
      </Button>

      {isOpen ? (
        <div
          role="menu"
          // Sombra forte e fundo solido: o menu flutua sobre a grade da agenda
          // sem deixar duvida do que esta na frente.
          className="absolute left-0 z-30 mt-2 w-64 overflow-hidden rounded-xl border border-border bg-background shadow-xl ring-1 ring-black/5"
        >
          <button
            type="button"
            role="menuitem"
            className="flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-muted"
            onClick={() => {
              setOpen(false);
              onNewAppointment();
            }}
          >
            <CalendarPlus className="mt-0.5 h-5 w-5 text-muted-foreground" />
            <span>
              <span className="block font-medium">Agendamento</span>
              <span className="block text-sm text-muted-foreground">
                Marcar o horario agora mesmo
              </span>
            </span>
          </button>

          <button
            type="button"
            role="menuitem"
            className="flex w-full items-start gap-3 border-t border-border px-4 py-3 text-left transition-colors hover:bg-muted"
            onClick={() => {
              setOpen(false);
              onNewLink();
            }}
          >
            <Link2 className="mt-0.5 h-5 w-5 text-muted-foreground" />
            <span>
              <span className="block font-medium">Link de agendamento</span>
              <span className="block text-sm text-muted-foreground">
                O cliente escolhe pelo WhatsApp
              </span>
            </span>
          </button>
        </div>
      ) : null}
    </div>
  );
}
